import { Fragment, useMemo, type ReactNode } from 'react';
import { parseWhatsAppText, type ParseOptions, type WaNode } from './parse.js';
import './whatsapp-text.css';

/**
 * A WhatsApp message body, rendered.
 *
 * React elements, never `dangerouslySetInnerHTML`: a body is text a stranger
 * typed, so it goes through JSX and gets escaped like any other string. The
 * only attribute it can influence is a link's `href`, and links.ts allows four
 * schemes through, none of them `javascript:`.
 */
export interface WhatsAppTextProps extends ParseOptions {
  /** The body as WhatsApp stores it, markers and all. */
  value: string | null | undefined;
  className?: string;
  /** Shown when the body is empty. */
  fallback?: ReactNode;
}

export function WhatsAppText({ value, className, fallback = null, links, maxLinkChars }: WhatsAppTextProps) {
  const nodes = useMemo(
    () => parseWhatsAppText(value, { links, maxLinkChars }),
    [value, links, maxLinkChars]
  );
  if (nodes.length === 0) return <>{fallback}</>;
  return (
    <span className={className ? `rr-watext ${className}` : 'rr-watext'}>
      <Nodes nodes={nodes} />
    </span>
  );
}

function Nodes({ nodes }: { nodes: WaNode[] }) {
  return (
    <>
      {nodes.map((n, i) => (
        <Fragment key={i}>
          <Node node={n} />
        </Fragment>
      ))}
    </>
  );
}

function Node({ node }: { node: WaNode }) {
  switch (node.type) {
    case 'text':
      return <>{node.value}</>;
    case 'bold':
      return <strong><Nodes nodes={node.children} /></strong>;
    case 'italic':
      return <em><Nodes nodes={node.children} /></em>;
    case 'strike':
      return <s className="rr-watext__strike"><Nodes nodes={node.children} /></s>;
    case 'code':
      return <code className="rr-watext__code">{node.value}</code>;
    case 'codeblock':
      // A block, but still an inline element: a bubble body is a paragraph and
      // a <pre> inside a <span> is not valid nesting.
      return <code className="rr-watext__block">{node.value}</code>;
    case 'link':
      return (
        <a
          className="rr-watext__link"
          href={node.href}
          title={node.title}
          target="_blank"
          rel="noopener noreferrer nofollow ugc"
        >
          {node.text}
        </a>
      );
    default:
      return null;
  }
}
